import React, { useEffect, useState } from "react";
import { PhoneCall, Loader2, Trash2, RefreshCw, Play, Pause, Sparkles } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const VOICES = [
  { id: "alloy",   label: "Alloy — neutral / friendly" },
  { id: "onyx",    label: "Onyx — male, baritone" },
  { id: "echo",    label: "Echo — male, warm" },
  { id: "fable",   label: "Fable — British storyteller" },
  { id: "shimmer", label: "Shimmer — female, warm" },
  { id: "nova",    label: "Nova — female, energetic" },
];

const STYLES = ["excited fan", "sleepy insomniac", "gruff trucker", "chill college kid", "hyped teen", "cool DJ friend", "midnight caller", "morning commuter"];

/**
 * Admin card: generate AI listener call-ins per station. Claude writes a short
 * caller line, OpenAI TTS voices it in a picked persona voice, drop injects
 * into the station rotation every N tracks (server-controlled cadence).
 */
export default function AICallersCard() {
  const [stations, setStations] = useState([]);
  const [callers, setCallers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [form, setForm] = useState({
    station_id: "", caller_name: "", city: "", voice: "alloy",
    style: "excited fan", topic: "", script: "",
  });
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/stations"),
        api.get("/admin/callers", { headers: authHeaders() }),
      ]);
      setStations(s.data || []);
      setCallers(c.data || []);
      if (!form.station_id && (s.data || [])[0]) setForm(f => ({ ...f, station_id: s.data[0].station_id }));
    } catch (_) { /* keep silent — user sees empty state */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setErr("");
    if (!form.station_id || !form.caller_name.trim()) { setErr("Station and caller name are required."); return; }
    setGenerating(true);
    try {
      await api.post("/admin/callers/generate", form, { headers: authHeaders() });
      setForm({ ...form, caller_name: "", city: "", topic: "", script: "" });
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const play = (c) => {
    const el = document.getElementById(`caller-audio-${c.caller_id}`);
    if (!el) return;
    if (nowPlaying === c.caller_id) { el.pause(); setNowPlaying(null); return; }
    document.querySelectorAll("audio[data-caller]").forEach(a => a.pause());
    el.play().then(() => setNowPlaying(c.caller_id)).catch(() => {});
    el.onended = () => setNowPlaying(null);
  };

  const del = async (c) => {
    if (!window.confirm(`Delete caller "${c.caller_name}"?`)) return;
    try { await api.delete(`/admin/callers/${c.caller_id}`, { headers: authHeaders() }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const stationName = (id) => stations.find(s => s.station_id === id)?.name || id;

  return (
    <section className="fl-card p-6 mt-6" data-testid="ai-callers-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
          <PhoneCall className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="fl-display text-xl font-bold text-white">AI callers — Fliboe FM</h2>
          <p className="text-white/50 text-sm mt-0.5">Generate short listener call-ins. They inject between music tracks so your station sounds alive.</p>
        </div>
        <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="callers-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <select className="fl-input" value={form.station_id} onChange={e => setForm({ ...form, station_id: e.target.value })} data-testid="callers-station">
          {stations.length === 0 && <option value="">No stations yet</option>}
          {stations.map(s => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
        </select>
        <input className="fl-input" placeholder="Caller name (e.g. Marcus)" value={form.caller_name} onChange={e => setForm({ ...form, caller_name: e.target.value })} data-testid="callers-name" />
        <input className="fl-input" placeholder="City (optional)" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="callers-city" />
        <select className="fl-input" value={form.voice} onChange={e => setForm({ ...form, voice: e.target.value })} data-testid="callers-voice">
          {VOICES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
        <select className="fl-input" value={form.style} onChange={e => setForm({ ...form, style: e.target.value })} data-testid="callers-style">
          {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input className="fl-input" placeholder="Topic hint (optional — e.g. 'summer road trip')" value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} data-testid="callers-topic" />
        <textarea className="fl-input md:col-span-2" rows={2} placeholder="Custom script (optional — leave blank to let Claude write it)" value={form.script} onChange={e => setForm({ ...form, script: e.target.value.slice(0, 800) })} data-testid="callers-script" />
      </div>

      <button onClick={generate} disabled={generating || !form.station_id} className="fl-btn mt-4" data-testid="callers-generate">
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {generating ? "Generating…" : "Generate caller"}
      </button>
      {err && <div className="text-red-300 text-sm mt-2" data-testid="callers-err">{err}</div>}

      {/* Existing callers */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">On-air callers · {callers.length}</div>
        {callers.length === 0 ? (
          <div className="text-white/40 text-sm">No callers yet. Generate one above.</div>
        ) : (
          <div className="space-y-2">
            {callers.map(c => (
              <div key={c.caller_id} className="fl-card p-3 flex items-center gap-3 flex-wrap" data-testid={`caller-${c.caller_id}`}>
                <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center shrink-0">
                  <PhoneCall className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-semibold truncate">{c.caller_name}{c.city ? ` · ${c.city}` : ""}</div>
                  <div className="text-white/40 text-[11px] truncate">On {stationName(c.station_id)} · {Math.round(c.duration || 0)}s · {c.voice}</div>
                  <div className="text-white/60 text-xs mt-1 line-clamp-2">&ldquo;{c.script}&rdquo;</div>
                </div>
                <audio id={`caller-audio-${c.caller_id}`} data-caller src={resolveAudio(`/api/audio/${c.audio_filename}`)} preload="none" />
                <button onClick={() => play(c)} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`caller-play-${c.caller_id}`}>
                  {nowPlaying === c.caller_id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => del(c)} className="fl-btn ghost !py-1.5 !px-2.5 hover:!border-red-400/40 hover:!text-red-300" data-testid={`caller-delete-${c.caller_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
